// ============================================================
// @bienlivrer/types — Types partagés entre API, Web et Mobile
// ============================================================

// ── Enums ────────────────────────────────────────────────────
export type UserRole = 'customer' | 'driver' | 'admin' | 'merchant' | 'cs_agent';
export type Language = 'fr' | 'de' | 'en' | 'it';

/**
 * Source de vérité unique pour les catégories commerce.
 * Ordre canonique — ne pas réordonner sans migration données + front.
 * Exclut volontairement la restauration (pas de concurrence food delivery).
 */
export const SHOP_CATEGORY_VALUES = [
  'general_store',
  'convenience',
  'bakery',
  'grocery',
  'butcher',
  'bookstore',
  'florist',
  'pharmacy',
  'petshop',
  'electronics',
  'fashion',
  'home_goods',
  'office',
  'baby_kids',
  'sports',
  'hardware',
  'cosmetics',
  'other',
] as const;

export type ShopCategory = (typeof SHOP_CATEGORY_VALUES)[number];

export type ShopStatus = 'pending' | 'active' | 'suspended' | 'closed';
export type ShopSubscription = 'starter' | 'pro' | 'premium';

/** Plafond d’épinglage vitrine (offre payante + pack fondateur, stock limité). */
export const MAX_HOME_PINNED_SHOPS = 3;
/** Places « Commerçant fondateur » Biel/Bienne. */
export const FOUNDER_PROGRAMME_MAX = 15;
/** Durée d’épinglage du pack ouverture fondateur (jours calendaires). */
export const FOUNDER_LAUNCH_PIN_DAYS = 14;
/** Durées vendables d’une épingle payée : 1, 2 ou 3 semaines, ou 30 jours. */
export const PAID_HOME_PIN_DAY_OPTIONS = [7, 14, 21, 30] as const;
export type PaidHomePinDays = (typeof PAID_HOME_PIN_DAY_OPTIONS)[number];
/** Épingle (3 places) : 7 / 14 / 21 / 30 jours. */
export const HOME_PIN_PRICE_CHF: Record<PaidHomePinDays, number> = {
  7: 29,
  14: 55,
  21: 75,
  30: 90,
};
/** Vedette, une seule affiche : environ trois fois l’épingle. */
export const FEATURED_PRICE_CHF: Record<PaidHomePinDays, number> = {
  7: 99,
  14: 175,
  21: 230,
  30: 299,
};
/** Repli si une file payée n’a pas de durée enregistrée (offre 30 j). */
export const PAID_HOME_PIN_DAYS = 30;

export function isPaidHomePinDays(value: number): value is PaidHomePinDays {
  return (PAID_HOME_PIN_DAY_OPTIONS as readonly number[]).includes(value);
}

export type MerchantVisibilityKind = 'pin' | 'featured';

export function visibilityPriceChf(kind: MerchantVisibilityKind, days: PaidHomePinDays): number {
  return kind === 'pin' ? HOME_PIN_PRICE_CHF[days] : FEATURED_PRICE_CHF[days];
}
/** Rotation vedette parmi les fondateurs actuellement épinglés (jours). */
export const FOUNDER_FEATURED_TURN_DAYS = 2;

export type PromotionReviewStatus = 'none' | 'pending' | 'rejected';

export type ProductUnit = 'piece' | 'kg' | 'liter' | 'lot' | 'pack';
export type ProductStatus = 'active' | 'draft' | 'out_of_stock' | 'archived';

export type OrderType = 'delivery' | 'click_collect';
export type OrderStatus =
  | 'pending_payment'
  | 'pending'
  | 'accepted'
  | 'ready'
  | 'pickup'
  | 'delivering'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'refunded';

/**
 * `twint` est conservé uniquement pour la lecture des paiements historiques créés avant la
 * migration du 18/08/2026 (mock TWINT interne, jamais branché à un vrai PSP). Depuis cette date,
 * TWINT est traité via Stripe (`method: 'stripe'`, PaymentElement) — aucun nouveau paiement ne
 * doit plus être créé avec `method: 'twint'`. Voir docs/runbooks/twint-integration-plan.md.
 */
export type PaymentMethod = 'stripe' | 'twint' | 'qr_bill' | 'cash';
export type PaymentStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'refunded';

export type DeliveryStatus =
  | 'searching'
  | 'awaiting_manual'
  | 'pending_acceptance'
  | 'assigned'
  | 'heading_to_shop'
  | 'at_shop'
  | 'picked_up'
  | 'delivering'
  | 'delivered'
  | 'failed';

// ── Utilisateurs ─────────────────────────────────────────────
export interface User {
  id: string;
  email: string;
  phone?: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  language: Language;
  emailVerified: boolean;
  phoneVerified: boolean;
  isVerified: boolean;
  isActive: boolean;
  /** Compte commerçant démo (parcours vitrine, lecture seule). */
  isDemo?: boolean;
  /** Catégorie de boutique choisie en mode démo commerçant. */
  demoPreviewCategory?: ShopCategory | null;
  /** Nouvelle adresse email en attente de confirmation (lien envoyé). */
  pendingEmailChange?: string | null;
  avatarUrl?: string;
  createdAt: string;
}

export interface UserProfile extends User {
  defaultAddress?: Address;
}

// ── Adresse ──────────────────────────────────────────────────
export interface Address {
  street: string;
  zip: string;
  city: string;
  lat?: number;
  lng?: number;
}

// ── Horaires ─────────────────────────────────────────────────
export interface DaySchedule {
  open?: string;   // "07:00"
  close?: string;  // "18:30"
  closed: boolean;
}

export interface OpeningHoursException {
  date: string; // ISO "2026-08-01"
  closed: boolean;
  reason?: string;
}

export interface OpeningHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  exceptions?: OpeningHoursException[];
}

// ── Commerce ─────────────────────────────────────────────────
export interface Shop {
  id: string;
  ownerId: string;
  nameFr: string;
  nameDe?: string;
  nameEn?: string;
  nameIt?: string;
  slug: string;
  category: ShopCategory;
  descriptionFr?: string;
  descriptionDe?: string;
  descriptionEn?: string;
  descriptionIt?: string;
  address: string;
  city: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  phone?: string;
  email?: string;
  websiteUrl?: string;
  logoUrl?: string;
  coverUrl?: string;
  subscription: ShopSubscription;
  commissionRate: number;
  /** Majoration % saisie (GET /shops/mine). En vitrine publique : majoration effective. */
  priceMarkupPercent?: number;
  /** Majoration réellement appliquée au client (0 pendant l’essai, plafonnée à la commission) */
  effectivePriceMarkupPercent?: number;
  /** Plafond = 0 en essai, sinon commissionRate (8 / 6 / 4) */
  markupMaxPercent?: number;
  /** false pendant l’essai 90 j (commission 0 %) */
  markupAllowed?: boolean;
  inTrial?: boolean;
  trialEndsAt?: string | null;
  /** Afficher l’indication « Prix commande en ligne » côté client (si majoration > 0) */
  showPriceMarkupBadge?: boolean;
  status: ShopStatus;
  preparationTimeMin: number;
  minOrderAmount: number;
  freeDeliveryAbove?: number;
  openingHours: OpeningHours;
  isVerified: boolean;
  ratingAvg: number;
  ratingCount: number;
  deliveryEnabled?: boolean;
  clickCollectEnabled?: boolean;
  cashEnabled?: boolean;
  createdAt: string;
  /** Commerce de démonstration (remplaçable par un partenaire réel) */
  isDemo?: boolean;
  /** Mis en avant manuellement (offre commerciale) */
  homePinned?: boolean;
  homePinnedUntil?: string | null;
  promotionFr?: string | null;
  promotionDe?: string | null;
  promotionEn?: string | null;
  promotionIt?: string | null;
  promotionUntil?: string | null;
  /** Champs review : uniquement GET /shops/mine et admin, jamais la vitrine publique. */
  promotionPendingFr?: string | null;
  promotionPendingDe?: string | null;
  promotionPendingEn?: string | null;
  promotionPendingIt?: string | null;
  promotionPendingUntil?: string | null;
  promotionReviewStatus?: PromotionReviewStatus;
  /** Commerçant fondateur — pack épinglage 14 j en file (max 15). */
  isFounder?: boolean;
  launchPinQueuedAt?: string | null;
  launchPinStartedAt?: string | null;
}

/** Réponse liste publique GET /shops */
export interface ShopsListResponse {
  data: unknown[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    home?: {
      announcement: { fr: string; de: string; en?: string; it?: string; link?: string; until?: string } | null;
      featuredShopId: string | null;
      featuredUntil?: string | null;
      newPartnerDays: number;
    };
  };
}

export interface ShopWithDistance extends Shop {
  distanceKm: number;
  isOpen: boolean;
  nextClose?: string;
  deliveryFeeFrom?: number;
  supportsDelivery: boolean;
  supportsPickup: boolean;
}

// ── Produit ──────────────────────────────────────────────────
export interface ProductImage {
  url: string;
  isPrimary: boolean;
}

export interface Product {
  id: string;
  shopId: string;
  nameFr: string;
  nameDe?: string;
  nameEn?: string;
  nameIt?: string;
  descriptionFr?: string;
  descriptionDe?: string;
  descriptionEn?: string;
  descriptionIt?: string;
  price: number;
  unit: ProductUnit;
  stockQuantity?: number;
  stockThreshold: number;
  categoryId?: string;
  images: ProductImage[];
  allergens: string[];
  vatRate: number;
  status: ProductStatus;
  sortOrder: number;
  createdAt: string;
}

export interface ProductCategory {
  id: string;
  shopId: string;
  nameFr: string;
  nameDe?: string;
  nameEn?: string;
  nameIt?: string;
  sortOrder: number;
}

export {
  PRODUCT_CATEGORY_TEMPLATES,
  PRODUCT_CATEGORY_TEMPLATE_COUNT,
  getProductCategoryTemplates,
  assertProductCategoryTemplatesComplete,
  type ProductCategoryTemplate,
} from './productCategoryTemplates.ts';

// ── Commande ─────────────────────────────────────────────────
export interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  productSnapshot: {
    nameFr: string;
    nameDe?: string;
    price: number;
    imageUrl?: string;
  };
  quantity: number;
  unitPrice: number;
  vatRate: number;
  totalPrice: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  /** Absent pour une commande invité (coordonnées dans guest*). */
  customerId: string | null;
  guestEmail?: string | null;
  guestFirstName?: string | null;
  guestLastName?: string | null;
  guestPhone?: string | null;
  guestLanguage?: string | null;
  shopId: string;
  status: OrderStatus;
  type: OrderType;
  deliveryAddress?: Address;
  scheduledAt?: string;
  acceptedAt?: string;
  readyAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  subtotal: number;
  deliveryFee: number;
  /** Course offerte par le magasin, déduite du reversement. */
  merchantCoveredDeliveryFee?: number;
  serviceFee: number;
  discountAmount: number;
  vatAmount: number;
  totalAmount: number;
  currency: string;
  notes?: string;
  pickupCode?: string;
  pickupQrUrl?: string;
  acceptedTermsAt?: string | null;
  termsVersion?: string | null;
  driverId?: string;
  ratingShop?: number;
  ratingDriver?: number;
  reviewComment?: string;
  items?: OrderItem[];
  shop?: Pick<Shop, 'id' | 'nameFr' | 'nameDe' | 'logoUrl' | 'address'>;
  createdAt: string;
}

// ── Paiement ─────────────────────────────────────────────────
export interface Payment {
  id: string;
  orderId: string;
  method: PaymentMethod;
  status: PaymentStatus;
  amount: number;
  currency: string;
  externalId?: string;
  qrBillRef?: string;
  qrBillPdfUrl?: string;
  qrBillDueAt?: string;
  refundAmount?: number;
  refundedAt?: string;
  createdAt: string;
}

// ── Livraison ─────────────────────────────────────────────────
export interface GpsPoint {
  lat: number;
  lng: number;
  ts: string;
}

export interface Delivery {
  id: string;
  orderId: string;
  driverId?: string;
  status: DeliveryStatus;
  pickupLat?: number;
  pickupLng?: number;
  deliveryLat?: number;
  deliveryLng?: number;
  distanceKm?: number;
  driverFee?: number;
  assignedAt?: string;
  pickedUpAt?: string;
  deliveredAt?: string;
  proofPhotoUrl?: string;
  trackingLog: GpsPoint[];
  createdAt: string;
}

// ── Avis ─────────────────────────────────────────────────────
export interface Review {
  id: string;
  orderId: string;
  customerId: string;
  shopId: string;
  driverId?: string;
  ratingShop: number;
  ratingDriver?: number;
  comment?: string;
  reply?: string;
  isVisible: boolean;
  createdAt: string;
  customer?: Pick<User, 'firstName' | 'lastName' | 'avatarUrl'>;
}

// ── Coupon ───────────────────────────────────────────────────
export type CouponType = 'percentage' | 'fixed_amount' | 'free_delivery';

export interface Coupon {
  id: string;
  shopId?: string;
  code: string;
  type: CouponType;
  value: number;
  minOrder: number;
  maxUses?: number;
  usedCount: number;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
}

// ── Réponses API génériques ───────────────────────────────────
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
  details?: Record<string, string[]>;
}

// ── Requêtes Auth ─────────────────────────────────────────────
export interface RegisterRequest {
  email: string;
  phone?: string;
  firstName: string;
  lastName: string;
  password: string;
  language: Language;
  role: UserRole;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

// ── Requêtes Commande ─────────────────────────────────────────
export interface CreateOrderRequest {
  shopId: string;
  type: OrderType;
  deliveryAddress?: Address;
  scheduledAt?: string;
  items: Array<{ productId: string; quantity: number }>;
  paymentMethod: PaymentMethod;
  couponCode?: string;
  notes?: string;
}

// ── Analytics Events ─────────────────────────────────────────
export type AnalyticsEventName =
  | 'app_open'
  | 'signup_started'
  | 'signup_completed'
  | 'login'
  | 'view_home'
  | 'search'
  | 'filter_applied'
  | 'view_shop'
  | 'view_product'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'start_checkout'
  | 'select_payment_method'
  | 'purchase'
  | 'checkout_abandoned'
  | 'order_accepted_by_merchant'
  | 'order_ready'
  | 'pickup'
  | 'delivery_start'
  | 'delivery_complete'
  | 'review_submitted'
  | 'support_ticket_created'
  | 'repeat_order'
  | 'referral_shared';

export interface AnalyticsEvent {
  eventName: AnalyticsEventName;
  properties?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
}
